import { useContext, useEffect } from "react";
import { get } from "lodash";
import "../styles/home-styles.css";
import { DotLoader } from "react-spinners";
import { ProductContext } from "../restaurant/Context";
import { useNavigate, useParams } from "react-router-dom";

/* ═══════════════════════════════════════════════════════
   NetliseMark — inline N logo, no white background rect.
   The Logo.svg has a white <rect> that turns into a solid
   white square when a CSS filter is applied. This inline
   component renders only the N paths with a colour prop.
═══════════════════════════════════════════════════════ */
const NetliseMark = ({
  size = 24,
  color = "currentColor",
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="34 36 62 61"
    style={{ display: "block", flexShrink: 0 }}
    aria-hidden="true"
  >
    {/* Original N paths from Logo.svg, white bg rect omitted */}
    <g transform="translate(-1002 -277)">
      <g transform="translate(1039.749 314.949)">
        <g transform="translate(-2 0)" fill={color}>
          <rect width="18" height="39" transform="translate(0.251 18.051)" />
          <rect width="19" height="40" transform="translate(39.251 17.051)" />
          <path
            d="M-.2.01,40,0V18l-40.2.02Z"
            transform="translate(18.251 0.051)"
          />
          <path
            d="M672,311.138h17.919v-18.17Z"
            transform="translate(-671.749 -292.917)"
          />
        </g>
      </g>
    </g>
  </svg>
);

/* ── Star icon ─────────────────────────────────────── */
const StarIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="#FFD700">
    <path d="M12 2.5l2.9 6.4 7 .7-5.2 4.7 1.5 6.9L12 17.6l-6.2 3.6 1.5-6.9L2 9.6l7-.7Z" />
  </svg>
);

/* ── Google G icon ──────────────────────────────────── */
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.4a10.6 10.6 0 0 1-4.6 6.9v5.8h7.5c4.4-4 6.8-10 6.8-16.7z"/>
    <path fill="#34A853" d="M24 47c6.2 0 11.4-2 15.2-5.6l-7.5-5.8c-2 1.4-4.7 2.2-7.7 2.2-5.9 0-10.9-4-12.7-9.3H3.6v6A23 23 0 0 0 24 47z"/>
    <path fill="#FBBC05" d="M11.3 28.5A13.8 13.8 0 0 1 10.6 24c0-1.6.3-3.1.7-4.5v-6H3.6A23 23 0 0 0 1 24c0 3.7.9 7.2 2.6 10.3l7.7-5.8z"/>
    <path fill="#EA4335" d="M24 10.2c3.3 0 6.3 1.1 8.6 3.4l6.4-6.4C35.4 3.5 30.2 1 24 1A23 23 0 0 0 3.6 13.7l7.7 6c1.8-5.4 6.8-9.5 12.7-9.5z"/>
  </svg>
);

/* ═══════════════════════════════════════════════════════
   URL helpers
═══════════════════════════════════════════════════════ */
const S3_BASE =
  "https://netlise-image-bucket.s3.ap-southeast-1.amazonaws.com/public/";
const CDN = "https://dm6mmpk56rvc9.cloudfront.net/";

/* ═══════════════════════════════════════════════════════
   ShopBadge — shop logo (S3) or Netlise N fallback.
   Used both standalone (no cover) and as the overlapping
   badge on top of the cover photo.
═══════════════════════════════════════════════════════ */
const ShopBadge = ({ shop, small = false }: { shop: any; small?: boolean }) => {
  const logoUrl = shop?.image ? `${S3_BASE}${shop.image}` : null;
  const name: string = shop?.shopName || "";
  const wrapClass = small ? "welcome-shop-logo-wrap welcome-shop-logo-wrap--sm" : "welcome-shop-logo-wrap";
  const badgeClass = small ? "welcome-netlise-badge welcome-netlise-badge--sm" : "welcome-netlise-badge";

  if (logoUrl) {
    return (
      <div className={wrapClass}>
        <img
          src={logoUrl}
          alt={name}
          className="welcome-shop-logo"
          onError={(e) => {
            const el = e.currentTarget.parentElement as HTMLElement;
            if (el) el.style.display = "none";
          }}
        />
      </div>
    );
  }

  return (
    <div className={badgeClass}>
      <NetliseMark size={small ? 26 : 38} color="#ffffff" />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   MenuCover — full-width restaurant photo at top of card.
   Uses shop.menuImage via CloudFront CDN.
   Shows the shop logo badge overlapping the bottom edge.
═══════════════════════════════════════════════════════ */
const MenuCover = ({ shop }: { shop: any }) => {
  const coverUrl = shop?.menuImage ? `${CDN}${shop.menuImage}` : null;
  if (!coverUrl) return null;

  return (
    <div className="welcome-cover">
      <img
        src={coverUrl}
        alt={shop?.shopName || "Restaurant"}
        className="welcome-cover-img"
        onError={(e) => {
          /* hide the whole cover section if image fails */
          const wrap = e.currentTarget.closest(".welcome-cover") as HTMLElement;
          if (wrap) wrap.style.display = "none";
        }}
      />
      {/* dark gradient at the bottom so badge is always legible */}
      <div className="welcome-cover-scrim" />
      {/* shop logo badge sits at the bottom-left of the cover */}
      <div className="welcome-cover-badge">
        <ShopBadge shop={shop} small />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   WelcomeView
═══════════════════════════════════════════════════════ */
const WelcomeView = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { shop, setQrId, loadingMenu, setShortCode, fetchDataForQrId } =
    useContext(ProductContext);

  useEffect(() => {
    if (params && params.shortCode) {
      let qrId: string;
      if (params.id) {
        qrId = `${params.shortCode}/${params.id}`;
        setQrId(qrId);
      } else {
        qrId = `${params.shortCode}`;
      }
      fetchDataForQrId(qrId);
      setShortCode(params.shortCode);
    }
  }, [params, navigate]);

  const onClickMenu = () => {
    if (shop) {
      navigate(`/restaurant/${shop.shortCode}`);
    }
  };

  const shopName: string = get(shop, "shopName", "");
  const rating: number | null = get(shop, "rating", null);
  const address: string = get(shop, "address", "");
  const hasCover: boolean = Boolean(shop?.menuImage);
  const googleLink: string | null = get(shop, "googleLink", null) || null;

  return (
    <div className="welcome-wrapper">
      {/* Ambient glow effects */}
      <div className="welcome-glow-tl" />
      <div className="welcome-glow-br" />

      {/* Netlise wordmark — top left, proper N logo (no white square) */}
      <div className="welcome-logo-wrap">
        <NetliseMark size={26} color="#ffffff" />
        <span className="welcome-logo-wordmark">netlise</span>
      </div>

      {/* Vertically-centred content */}
      <div className="welcome-center">
        {/* Glassmorphism merchant card */}
        <div className={`welcome-merchant-card${hasCover ? " welcome-merchant-card--cover" : ""}`}>

          {/* Restaurant cover photo (menuImage via CloudFront) — shown when available */}
          <MenuCover shop={shop} />

          {/* Standalone badge — only shown when there is no cover photo */}
          {!hasCover && <ShopBadge shop={shop} />}

          {/* Card text body — has its own padding when cover is above */}
          <div className={hasCover ? "welcome-card-body" : ""}>
            <div className="welcome-card-label">Welcome to</div>

            {shopName ? (
              <div className="welcome-card-name">{shopName}</div>
            ) : (
              <div className="welcome-card-name-skeleton" />
            )}

            <div className="welcome-card-meta">
              {rating != null && rating > 0 && (
                <>
                  <span className="welcome-meta-rating">
                    <StarIcon />
                    {rating}
                  </span>
                  <span className="welcome-card-meta-dot" />
                </>
              )}
              {address && <span className="welcome-card-meta-address">{address}</span>}
            </div>
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={onClickMenu}
          className="welcome-cta-btn"
          disabled={loadingMenu}
        >
          {loadingMenu ? (
            <DotLoader color="#fff" loading size={18} />
          ) : (
            "See menu →"
          )}
        </button>

        {/* Google Review — only shown when shop.googleLink is set */}
        {googleLink && (
          <a
            href={googleLink}
            target="_blank"
            rel="noopener noreferrer"
            className="welcome-google-btn"
          >
            <GoogleIcon />
            <span>Leave a Google Review</span>
          </a>
        )}

        <div className="welcome-past-visits">Your past visits</div>
      </div>

      {/* Footer */}
      <div className="welcome-footer">
        <span>Powered by</span>
        <NetliseMark size={13} color="rgba(255,255,255,0.50)" />
        <span className="welcome-footer-wordmark">netlise</span>
      </div>
    </div>
  );
};

export default WelcomeView;
